import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CreationHero, type HeroSeed } from '../components/CreationHero'
import { StreamRail } from '../components/StreamRail'
import { AttentionStrip } from '../../autopilot/components/AttentionStrip'
import { WeekFloor } from '../../autopilot/components/WeekFloor'
import { useAppSelector } from 'modules/core'
import { selectBusinessId } from 'modules/authentication'
import { safeGetLocalStorage, safeSetLocalStorage } from 'modules/core/services/safe-local-storage'
import { useIdeaStore } from '../store/idea-store'
import { useTopicStore } from '../store/topic-store'

const WEEK_EXPANDED_KEY = 'taka:homeWeekExpanded'

/**
 * The creation moment: Home is one creation surface with the week at the
 * floor. Attention strip (only when something needs the user) → CreationHero
 * (the "Tell me" door) → WeekFloor (summary strip + expandable cockpit).
 * The topic switcher + grid were retired with this page — topics live on
 * server-side, seeding the hero's "From your world" chips.
 * Design: playbook/product/home-creation-moment/DESIGN.md.
 */
export function HomePage() {
  const { track } = useAnalytics()
  const isMobile = useIsMobile()
  const fetchTopics = useTopicStore(s => s.fetchTopics)
  const weekRef = useRef<HTMLElement>(null)
  const heroRef = useRef<HTMLElement>(null)
  const [streamSeed, setStreamSeed] = useState<HeroSeed | null>(null)

  const location = useLocation()
  // Collapse preference is PER BUSINESS: the bare legacy key leaked one
  // account's preference to the next login on a shared browser (day-0 audit,
  // 2026-07-05). businessId can hydrate after mount and changes on business
  // switch, so the keyed value is re-read whenever the key settles.
  const businessId = useAppSelector(selectBusinessId)
  const weekKey = businessId ? `${WEEK_EXPANDED_KEY}:${businessId}` : null
  const [weekExpanded, setWeekExpanded] = useState(
    () => (weekKey ? safeGetLocalStorage(weekKey) : null) !== '0'
  )
  useEffect(() => {
    // A ?plan= deep link must stay expanded even if the stored preference is
    // collapsed — the plan popover only exists inside the expanded cockpit.
    if (weekKey && !new URLSearchParams(window.location.search).has('plan')) {
      setWeekExpanded(safeGetLocalStorage(weekKey) !== '0')
    }
  }, [weekKey])

  // A `?plan=open` / `?plan=edit` deep link targets the cockpit's plan popover,
  // which only exists while the week floor is expanded. Reacting to the search
  // string (not just mount) covers client-side navigation onto /?plan=open
  // while Home is already mounted (e.g. the /autopilot/setup redirect).
  useEffect(() => {
    if (new URLSearchParams(location.search).has('plan')) setWeekExpanded(true)
  }, [location.search])

  const handleToggleWeek = useCallback(
    (next: boolean) => {
      setWeekExpanded(next)
      if (weekKey) safeSetLocalStorage(weekKey, next ? '1' : '0')
    },
    [weekKey]
  )

  const handleReviewApprovals = useCallback(() => {
    handleToggleWeek(true)
    // Let the cockpit mount before scrolling to it.
    requestAnimationFrame(() => {
      weekRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [handleToggleWeek])

  useEffect(() => {
    track(AnalyticsEvents.Home.HOME_VIEWED, { platform: isMobile ? 'mobile' : 'desktop' })
    // Topics keep generating server-side; on Home they seed the hero's
    // "From your world" chips (and the cold-start bootstrap stays alive).
    void fetchTopics()
  }, [track, fetchTopics, isMobile])

  // Refetch when the tab regains focus after the payload's embedded GCS
  // signed URLs (6h TTL) may have expired — otherwise a long-open Home
  // renders dead media (GCS 400 ExpiredToken). Throttled so quick
  // tab-switches don't hammer the endpoints; both stores are refreshed
  // (topics feed the ghost ideas, ideas feed the rail's draft cards).
  const fetchIdeas = useIdeaStore(s => s.fetchIdeas)
  useEffect(() => {
    const REFETCH_AFTER_MS = 5 * 60 * 1000
    // focus + visibilitychange fire back-to-back on tab return, and the
    // store's lastFetchedAt only advances on response; a local stamp keeps
    // one return = one refetch.
    let lastInvoked = 0
    const maybeRefetch = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastInvoked < 10_000) return
      const last = useTopicStore.getState().lastFetchedAt
      if (last === null || Date.now() - last > REFETCH_AFTER_MS) {
        lastInvoked = Date.now()
        void fetchTopics()
        fetchIdeas()
      }
    }
    document.addEventListener('visibilitychange', maybeRefetch)
    window.addEventListener('focus', maybeRefetch)
    return () => {
      document.removeEventListener('visibilitychange', maybeRefetch)
      window.removeEventListener('focus', maybeRefetch)
    }
  }, [fetchTopics, fetchIdeas])

  // A failed post no longer auto-retries on load. It burned a credit every
  // visit, and re-drafting reset the card to "generating" so the terminal
  // failed state (with its Retry) never had a chance to show — the post read as
  // "Drafting…" forever. The server-side reclaim now marks stuck generations
  // failed + refunds, and the card carries an explicit Retry; the user decides.

  const handleStreamPick = useCallback((card: { promptSeed: string }) => {
    setStreamSeed({ text: card.promptSeed, key: Date.now() })
    heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // Page order (Matan, 2026-07-05): the WEEK opens the page — businesses plan
  // and read their social presence in weeks, so the overview is the anchor and
  // the creation moment sits directly under it.
  const mainSections = (
    <>
      <AttentionStrip onReview={handleReviewApprovals} />
      <section ref={weekRef} className="scroll-mt-6">
        <WeekFloor expanded={weekExpanded} onToggle={handleToggleWeek} />
      </section>
      <section ref={heroRef} className="scroll-mt-6">
        <CreationHero streamSeed={streamSeed} />
      </section>
      <StreamRail onPick={handleStreamPick} />
    </>
  )

  // The chat is NOT resident here anymore: home gets the same AssistantDock
  // (one FAB, one floating window) as every other room, via AppLayout.
  if (isMobile) {
    return (
      <div className="flex h-full flex-col overflow-y-auto px-4 pt-4 pb-24">
        <div className="space-y-8">{mainSections}</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <div className="mx-auto w-full max-w-5xl space-y-10">{mainSections}</div>
    </div>
  )
}
